import React, { useState, useEffect } from 'react';
import { Moon, Sun } from 'lucide-react';
import { useToast } from './Toast';

const DarkModeToggle = ({ 
  size = 'default',
  showLabel = true,
  className = '',
  labelClassName = '',
  variant = 'toggle' // 'toggle' or 'button'
}) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage and system preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // Check system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const toast = useToast();

  // Apply dark mode on component mount
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    
    // Save to localStorage
    localStorage.setItem('theme', newDarkMode ? 'dark' : 'light');
    
    // Apply to document
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    toast.success(newDarkMode ? 'تم تفعيل الوضع الليلي' : 'تم إلغاء الوضع الليلي');
  };

  const sizeClasses = {
    small: 'w-9 h-9',
    default: 'w-12 h-7',
    large: 'w-14 h-8'
  };

  const iconSize = {
    small: 16,
    default: 20,
    large: 24
  };

  // Toggle variant (switch style)
  if (variant === 'toggle') {
    return (
      <label className={`relative inline-flex items-center cursor-pointer ${className}`}>
        <input 
          type="checkbox" 
          className="sr-only peer"
          checked={isDarkMode}
          onChange={toggleDarkMode}
        />
        <div className={`${sizeClasses[size]} bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[-1.25rem] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary-600`}></div>
        
        {/* Icon */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <Sun 
            size={iconSize[size]} 
            className="text-gray-400 w-full h-full p-1.5 transition-all peer-checked:opacity-0 peer-checked:scale-0" 
          />
          <Moon 
            size={iconSize[size]} 
            className="text-primary-600 w-full h-full p-1.5 transition-all opacity-0 scale-0 peer-checked:opacity-100 peer-checked:scale-100" 
          />
        </div>

        {/* Optional Label */}
        {showLabel && (
          <span className={`mr-3 text-sm font-medium transition-colors ${
            isDarkMode ? 'text-primary-600' : 'text-gray-600'
          } ${labelClassName}`}>
            {isDarkMode ? 'الوضع الليلي' : 'الوضع النهاري'}
          </span>
        )}
      </label>
    );
  }

  // Button variant (simple button style)
  return (
    <button
      onClick={toggleDarkMode}
      className={`
        p-2 rounded-lg transition-all duration-300 transform active:scale-95
        bg-gray-100 dark:bg-gray-800
        hover:bg-gray-200 dark:hover:bg-gray-700
        text-gray-600 dark:text-gray-400
        focus:outline-none focus:ring-2 focus:ring-offset-2
        focus:ring-primary-600 dark:focus:ring-offset-gray-900
        ${className}
      `}
      aria-label={isDarkMode ? 'تفعيل الوضع النهاري' : 'تفعيل الوضع الليلي'}
      aria-pressed={isDarkMode}
    >
      {isDarkMode ? (
        <Sun className={`${sizeClasses[size]} text-yellow-500`} />
      ) : (
        <Moon className={`${sizeClasses[size]} text-primary-600`} />
      )}
      
      {showLabel && (
        <span className={`mr-2 text-sm font-medium transition-colors ${
          isDarkMode ? 'text-primary-600' : 'text-gray-600'
        } ${labelClassName}`}>
          {isDarkMode ? 'الوضع الليلي' : 'الوضع النهاري'}
        </span>
      )}
    </button>
  );
};

export default DarkModeToggle;
